import axios from 'axios';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  Timestamp,
  orderBy,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  limit,
  addDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, COLLECTIONS } from './config';
import {
  startLiveClass as startHMSClass,
  endLiveClass as endHMSClass,
  createRoom,
} from './streaming';

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Get teacher's courses
export const getTeacherCourses = async (teacherId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COURSES),
      where('teacherId', '==', teacherId)
    );
    
    const snapshot = await getDocs(q);
    const courses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort in memory instead
    return courses.sort((a, b) => {
      const dateA = a.createdAt?.toDate() || new Date(0);
      const dateB = b.createdAt?.toDate() || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting teacher courses:', error);
    throw error;
  }
};

// Get teacher's assignments with submission details
export const getTeacherAssignments = async (teacherId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ASSIGNMENTS),
      where('teacherId', '==', teacherId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting teacher assignments:', error);
    throw error;
  }
};

// Get detailed submission information for a specific assignment
export const getAssignmentSubmissions = async (assignmentId) => {
  try {
    if (!assignmentId) {
      throw new Error('Assignment ID is required');
    }

    const submissionsRef = collection(db, 'assignments', assignmentId, 'submissions');
    const submissionsSnap = await getDocs(submissionsRef);
    
    const submissions = [];
    for (const doc of submissionsSnap.docs) {
      const submissionData = doc.data();
      // Get student details
      const studentRef = doc(db, COLLECTIONS.USERS, submissionData.studentId);
      const studentSnap = await getDoc(studentRef);
      
      submissions.push({
        id: doc.id,
        ...submissionData,
        student: studentSnap.exists() ? {
          id: studentSnap.id,
          ...studentSnap.data()
        } : null
      });
    }

    // Sort by submission date
    submissions.sort((a, b) => b.submittedAt - a.submittedAt);
    
    return submissions;
  } catch (error) {
    console.error('Error fetching assignment submissions:', error);
    throw error;
  }
};

// Grade a submission
export const gradeSubmission = async (assignmentId, submissionId, gradeData) => {
  try {
    const submissionRef = doc(db, 'assignments', assignmentId, 'submissions', submissionId);
    await updateDoc(submissionRef, {
      grade: gradeData.grade,
      feedback: gradeData.feedback,
      gradedBy: gradeData.teacherId,
      gradedAt: serverTimestamp(),
      status: 'graded'
    });

    return true;
  } catch (error) {
    console.error('Error grading submission:', error);
    throw error;
  }
};

// Get teacher's students
export const getTeacherStudents = async (teacherId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.USERS),
      where('role', '==', 'student')
    );

    const snapshot = await getDocs(q);
    const students = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      displayName: doc.data().displayName || 'Unknown Student'
    }));

    students.sort((a, b) => a.displayName.localeCompare(b.displayName));
    return students;
  } catch (error) {
    console.error('Error getting teacher students:', error);
    throw error;
  }
};

// Create new course
export const createNewCourse = async (courseData) => {
  try {
    const courseRef = doc(collection(db, COLLECTIONS.COURSES));
    await updateDoc(courseRef, {
      ...courseData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return courseRef.id;
  } catch (error) {
    console.error('Error creating course:', error);
    throw error;
  }
};

// Create new assignment
export const createNewAssignment = async (assignmentData) => {
  try {
    // Create a new document reference with auto-generated ID
    const assignmentRef = doc(collection(db, COLLECTIONS.ASSIGNMENTS));
    
    // Add the document with the provided data
    await setDoc(assignmentRef, {
      ...assignmentData,
      id: assignmentRef.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'active',
      submissionCount: 0
    });

    return assignmentRef.id;
  } catch (error) {
    console.error('Error creating assignment:', error);
    throw error;
  }
};

// Save a lesson plan with documents
export const saveLessonPlan = async (teacherId, lessonData, files = []) => {
  try {
    const lessonRef = doc(collection(db, COLLECTIONS.LESSONS));
    
    // Upload attached files
    const uploadedFiles = await Promise.all(files.map(async (file) => {
      const fileRef = ref(storage, `lessons/${lessonRef.id}/${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      return { name: file.name, url, type: file.type };
    }));

    // Save lesson data
    await setDoc(lessonRef, {
      ...lessonData,
      teacherId,
      files: uploadedFiles,
      status: 'pending',
      createdAt: Timestamp.now(),
      weekStartDate: Timestamp.fromDate(new Date(lessonData.weekStartDate))
    });

    return lessonRef.id;
  } catch (error) {
    console.error('Error saving lesson:', error);
    throw error;
  }
};

// Get teacher's lessons
export const getTeacherLessons = async (teacherId) => {
  try {
    if (!navigator.onLine) {
      console.warn('Currently offline. Attempting to fetch from cache...');
    }

    if (!teacherId) {
      console.error('No teacherId provided to getTeacherLessons');
      return [];
    }

    const q = query(
      collection(db, COLLECTIONS.LESSONS),
      where('teacherId', '==', teacherId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const lessons = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
    }));

    return lessons;
  } catch (error) {
    console.error('Error getting lessons:', error);
    // Return empty array instead of throwing to handle gracefully
    return [];
  }
};

// Start a live class
export const startLiveClass = async (teacherId, classData) => {
  try {
    // Create a room in Firestore
    const room = await createRoom(teacherId, classData.classId);
    
    // Create the live class document in Firestore
    const docRef = await addDoc(collection(db, COLLECTIONS.LIVE_CLASSES), {
      ...classData,
      roomId: room.id,
      status: 'active',
      startTime: serverTimestamp(),
      teacherId,
      endTime: null
    });

    return {
      classId: docRef.id,
      roomId: room.id
    };
  } catch (error) {
    console.error('Error starting live class:', error);
    throw error;
  }
};

// End a live class
export const endLiveClass = async (classId) => {
  try {
    const classRef = doc(db, COLLECTIONS.LIVE_CLASSES, classId);
    const classDoc = await getDoc(classRef);
    
    if (!classDoc.exists()) {
      throw new Error('Class not found');
    }

    await updateDoc(classRef, {
      status: 'ended',
      endedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error ending class:', error);
    throw error;
  }
};

// Listen to student questions in real-time
export const subscribeToQuestions = (classId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.LIVE_CLASSES, classId, COLLECTIONS.QUESTIONS),
    orderBy('timestamp', 'desc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const questions = [];
    snapshot.forEach(doc => {
      questions.push({ id: doc.id, ...doc.data() });
    });
    callback(questions);
  });
};

// Answer a student question
export const answerQuestion = async (classId, questionId, answer) => {
  try {
    const questionRef = doc(db, COLLECTIONS.LIVE_CLASSES, classId, COLLECTIONS.QUESTIONS, questionId);
    await updateDoc(questionRef, {
      answer,
      answeredAt: Timestamp.now(),
      status: 'answered'
    });
  } catch (error) {
    console.error('Error answering question:', error);
    throw error;
  }
};

// Get teacher's schedule
export const getTeacherSchedule = async (teacherId, startDate, endDate) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.SCHEDULE),
      where('teacherId', '==', teacherId)
    );
    
    const snapshot = await getDocs(q);
    const schedules = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter and sort in memory instead
    return schedules
      .filter(schedule => {
        const scheduleDate = schedule.date?.toDate() || new Date(schedule.date);
        return scheduleDate >= startDate && scheduleDate <= endDate;
      })
      .sort((a, b) => {
        const dateA = a.date?.toDate() || new Date(a.date);
        const dateB = b.date?.toDate() || new Date(b.date);
        return dateA - dateB;
      });
  } catch (error) {
    console.error('Error getting teacher schedule:', error);
    throw error;
  }
};

// Get teacher stats
export const getTeacherStats = async (teacherId) => {
  try {
    // Implement statistics gathering logic here
    return {
      totalStudents: 0,
      totalClasses: 0,
      totalAssignments: 0,
      averageAttendance: 0
    };
  } catch (error) {
    console.error('Error getting teacher stats:', error);
    throw error;
  }
};

// Get recent activities
export const getRecentActivities = async (teacherId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ACTIVITIES),
      where('teacherId', '==', teacherId),
      orderBy('timestamp', 'desc'),
      limit(10)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting recent activities:', error);
    throw error;
  }
};

// Calendar Events Collection
const CALENDAR_EVENTS = 'calendar_events';
const CALENDAR_NOTES = 'calendar_notes';

// Save a calendar event
export const saveCalendarEvent = async (teacherId, eventData) => {
  try {
    const eventRef = doc(collection(db, COLLECTIONS.CALENDAR_EVENTS));
    
    // Create a date object that combines the date and time
    const [hours, minutes] = eventData.time.split(':');
    const eventDate = new Date(eventData.date);
    eventDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    const eventDoc = {
      title: eventData.title,
      description: eventData.description,
      teacherId,
      date: toLocalDateStr(eventDate),
      time: eventData.time,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(eventRef, eventDoc);
    return eventRef.id;
  } catch (error) {
    console.error('Error saving calendar event:', error);
    throw error;
  }
};

// Get teacher's calendar events
export const getTeacherCalendarEvents = async (teacherId, startDate, endDate) => {
  try {
    const eventsRef = collection(db, COLLECTIONS.CALENDAR_EVENTS);
    const q = query(
      eventsRef,
      where('teacherId', '==', teacherId)
    );
    
    const snapshot = await getDocs(q);
    const events = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    }));

    // Filter dates in memory
    const startDateStr = toLocalDateStr(startDate instanceof Date ? startDate : new Date(startDate));
    const endDateStr = toLocalDateStr(endDate instanceof Date ? endDate : new Date(endDate));

    return events
      .filter(event => event.date >= startDateStr && event.date <= endDateStr)
      .sort((a, b) => {
        // First sort by date
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        // If same date, sort by time
        return (a.time || '').localeCompare(b.time || '');
      });
  } catch (error) {
    console.error('Error getting calendar events:', error);
    throw error;
  }
};

// Update a calendar event
export const updateCalendarEvent = async (eventId, eventData) => {
  try {
    const eventRef = doc(db, COLLECTIONS.CALENDAR_EVENTS, eventId);
    
    const updateData = {
      ...eventData,
      updatedAt: serverTimestamp()
    };

    if (eventData.date && eventData.time) {
      const [hours, minutes] = eventData.time.split(':');
      const eventDate = new Date(eventData.date);
      eventDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      updateData.date = toLocalDateStr(eventDate);
    }

    await updateDoc(eventRef, updateData);
  } catch (error) {
    console.error('Error updating calendar event:', error);
    throw error;
  }
};

// Delete a calendar event
export const deleteCalendarEvent = async (teacherId, eventId) => {
  try {
    const eventRef = doc(db, COLLECTIONS.CALENDAR_EVENTS, eventId);
    // First verify the event belongs to the teacher
    const eventDoc = await getDoc(eventRef);
    if (!eventDoc.exists()) {
      throw new Error('Event not found');
    }
    const eventData = eventDoc.data();
    if (eventData.teacherId !== teacherId) {
      throw new Error('Unauthorized to delete this event');
    }
    await deleteDoc(eventRef);
  } catch (error) {
    console.error('Error deleting calendar event:', error);
    throw error;
  }
};

// Save a calendar note
export const saveCalendarNote = async (teacherId, date, note) => {
  try {
    const noteRef = doc(collection(db, COLLECTIONS.CALENDAR_NOTES));
    await updateDoc(noteRef, {
      teacherId,
      date,
      note,
      updatedAt: Timestamp.now()
    });
    return noteRef.id;
  } catch (error) {
    console.error('Error saving calendar note:', error);
    throw error;
  }
};

// Get teacher's calendar notes
export const getTeacherCalendarNotes = async (teacherId, startDate, endDate) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.CALENDAR_NOTES),
      where('teacherId', '==', teacherId),
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    
    const snapshot = await getDocs(q);
    const notes = {};
    snapshot.docs.forEach(doc => {
      notes[doc.data().date] = doc.data().note;
    });
    return notes;
  } catch (error) {
    console.error('Error getting calendar notes:', error);
    throw error;
  }
};

// Create a Daily.co room and store in Firebase
export const createDailyRoom = async (teacherId, roomData) => {
  try {
    const roomName = roomData.customUrl
      ? `custom-${Date.now()}`
      : `Lingoply-${Math.random().toString(36).substring(2, 10)}`;
    const url = roomData.customUrl || `https://meet.jit.si/${roomName}`;

    const roomRef = await addDoc(collection(db, COLLECTIONS.DAILYCO_ROOMS), {
      roomName,
      teacherId,
      title: roomData.title,
      description: roomData.description,
      subject: roomData.subject,
      startTime: roomData.startTime,
      teacherName: roomData.teacherName,
      platform: roomData.platform || 'jitsi',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      url
    });

    return {
      roomId: roomRef.id,
      roomName,
      url
    };
  } catch (error) {
    console.error('Error creating live room:', error);
    throw error;
  }
};

// End a Daily.co room
export const endDailyRoom = async (roomId) => {
  try {
    // Get the room details first
    const roomRef = doc(db, COLLECTIONS.DAILYCO_ROOMS, roomId);
    const roomDoc = await getDoc(roomRef);
    
    if (!roomDoc.exists()) {
      throw new Error('Room not found');
    }

    const roomData = roomDoc.data();
    const roomName = roomData.roomName;

    // Use Daily.co API token directly
    const DAILY_API_TOKEN = process.env.REACT_APP_DAILY_API_KEY;

    // Call Daily.co API to delete the room
    await axios.delete(`https://api.daily.co/v1/rooms/${roomName}`, {
      headers: {
        'Authorization': `Bearer ${DAILY_API_TOKEN}`,
        'Content-Type': 'application/json'
      }
    });

    // Update Firestore document
    await updateDoc(roomRef, {
      status: 'ended',
      endedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error ending Daily.co room:', error);
    throw error;
  }
};

export const getAllTeachers = async () => {
  try {
    if (!COLLECTIONS.USERS) {
      throw new Error('Users collection is not defined in COLLECTIONS');
    }

    const teachersRef = collection(db, COLLECTIONS.USERS);
    const q = query(
      teachersRef, 
      where('role', '==', 'teacher'),
      orderBy('displayName', 'asc')  // Sort teachers by name
    );
    
    const querySnapshot = await getDocs(q);
    const teachers = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      displayName: doc.data().displayName || 'Unknown Teacher'  // Fallback name
    }));

    console.log('Fetched teachers:', teachers);
    return teachers;
  } catch (error) {
    console.error('Error getting teachers:', error);
    throw error;
  }
};
